import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { blogPosts, categories } from '../../../mocks/blogPosts';

const BlogPostSidebar = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (!post) return null;

  // Get related posts (exclude current post, same category first)
  const relatedPosts = blogPosts
    .filter(p => p.slug !== slug)
    .sort((a, b) => {
      if (a.category === post.category && b.category !== post.category) return -1;
      if (a.category !== post.category && b.category === post.category) return 1;
      return 0;
    })
    .slice(0, 3);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('email', email);
      
      const response = await fetch('https://readdy.ai/api/form/d6k9h3f51pef945vq70g', {
        method: 'POST',
        body: new URLSearchParams(formData as any)
      });

      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
      }
    } catch (error) {
      console.error('Newsletter subscription failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <aside className="space-y-8">
      {/* Author Bio */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
        <div className="flex items-start space-x-4">
          <img 
            src={post.author.avatar} 
            alt={post.author.name}
            className="w-16 h-16 rounded-full object-cover"
          />
          <div>
            <h3 className="font-semibold text-gray-900 text-lg mb-2">{post.author.name}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {post.category === 'Cybersecurity' 
                ? 'Cybersecurity expert with over 10 years of experience in threat detection and enterprise security solutions.'
                : post.category === 'Cloud'
                ? 'Cloud solutions architect specializing in multi-cloud strategies and enterprise infrastructure optimization.'
                : post.category === 'AI & Analytics'
                ? 'Data scientist and AI researcher focused on machine learning applications in business intelligence and automation.'
                : 'Digital transformation strategist helping organizations navigate technology adoption and organizational change.'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 text-lg mb-4">Categories</h3>
        <div className="space-y-2">
          {categories.slice(1).map((category) => {
            const categoryCount = blogPosts.filter(p => p.category === category).length;
            return (
              <Link
                key={category}
                to={`/blog?category=${encodeURIComponent(category)}`}
                className="flex items-center justify-between py-2 px-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors group"
              >
                <span className="group-hover:text-teal-600 transition-colors">{category}</span>
                <span className="text-sm text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                  {categoryCount}
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Related Posts */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 text-lg mb-4">Related Articles</h3>
        <div className="space-y-4">
          {relatedPosts.map((relatedPost) => (
            <Link
              key={relatedPost.id}
              to={`/blog/${relatedPost.slug}`}
              className="block group"
            >
              <div className="flex space-x-3">
                <img 
                  src={relatedPost.coverImage} 
                  alt={relatedPost.title}
                  className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <span className="inline-block text-xs font-medium text-teal-600 bg-teal-50 px-2 py-1 rounded-full mb-2">
                    {relatedPost.category}
                  </span>
                  <h4 className="font-medium text-gray-900 group-hover:text-teal-600 transition-colors line-clamp-2 text-sm leading-tight">
                    {relatedPost.title}
                  </h4>
                  <p className="text-xs text-gray-500 mt-2">{relatedPost.readTime}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Newsletter Signup */}
      <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border border-teal-100 p-6">
        <div className="text-center mb-4">
          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-mail-line text-xl text-teal-600"></i>
          </div>
          <h3 className="font-semibold text-gray-900 text-lg">Stay Updated</h3>
          <p className="text-gray-600 text-sm mt-2">
            Get the latest insights and industry updates delivered to your inbox.
          </p>
        </div>
        
        {isSubmitted ? (
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-check-line text-xl text-green-600"></i>
            </div>
            <p className="text-green-700 font-medium">Thanks for subscribing!</p>
            <p className="text-green-600 text-sm">You'll receive our latest updates soon.</p>
          </div>
        ) : (
          <form onSubmit={handleNewsletterSubmit} data-readdy-form className="space-y-3">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting || !email}
              className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe Now'}
            </button>
          </form>
        )}
      </div>
    </aside>
  );
};

export default BlogPostSidebar;